import { debounce } from './debounce.js';

// Globaler Abstandswert, damit Sticky-Elemente vom Header fernbleiben
const BASE_SCROLL_OFFSET = 24;

function getElementHeight(el) {
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  if (rect && rect.height) return rect.height;
  return el.offsetHeight || 0;
}

function getAnnouncementHeight() {
  const announcementBar = document.querySelector('[data-announcement-bar]');
  return getElementHeight(announcementBar);
}

function getAnnouncementOffsetHeight() {
  return getAnnouncementHeight();
}

function getAnnouncementFixedHeight() {
  return getAnnouncementHeight();
}

function escapeSelector(value) {
  if (!value) return value;
  if (window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(value);
  }
  return value.replace(/["'\\\s#.:]/g, '\\$&');
}

function getTabsListHeight(el) {
  if (!el) return 0;
  const tabPanel = el.matches('[role="tabpanel"]') ? el : el.closest('[role="tabpanel"]');
  if (!tabPanel) return 0;
  const tabsContainer = tabPanel.closest('.tabs');
  if (!tabsContainer) return 0;
  const list = tabsContainer.querySelector('.tabs__list');
  if (!list) return 0;
  return list.getBoundingClientRect().height || list.offsetHeight || 0;
}

// Ermittelt die Höhe von Header und Produkt-Navigation und schreibt sie als CSS-Variable
function computeScrollOffset() {
  const header = document.querySelector('.site-header');
  const productNav = document.querySelector('.product-navigation, .product-navigation-legacy');
  const headerHeight = getElementHeight(header);
  const navHeight = getElementHeight(productNav);
  const offset = Math.max(0, Math.round(headerHeight + navHeight + BASE_SCROLL_OFFSET));
  document.documentElement.style.setProperty('--page-scroll-offset', `${offset}px`);
  return offset;
}

export function updateScrollOffset() {
  return computeScrollOffset();
}

export { getTabsListHeight };

// Setzt die Höhe des Headers als CSS-Variable
export function setHeaderHeight() {
  const header = document.querySelector('.site-header');
  const announcementHeight = getAnnouncementHeight();
  const announcementOffsetHeight = getAnnouncementOffsetHeight();
  const announcementFixedHeight = getAnnouncementFixedHeight();
  const headerHeight = header ? header.getBoundingClientRect().height : 0;

  document.documentElement.style.setProperty(
    '--site-header-base-height',
    `${Math.floor(headerHeight)}px`
  );
  document.documentElement.style.setProperty(
    '--announcement-bar-height',
    `${Math.floor(announcementOffsetHeight)}px`
  );
  document.documentElement.style.setProperty(
    '--announcement-bar-fixed-height',
    `${Math.floor(announcementFixedHeight)}px`
  );

  if (header) {
    document.documentElement.style.setProperty(
      '--site-header-height',
      `${Math.floor(headerHeight + announcementOffsetHeight)}px`
    );
  } else {
    document.documentElement.style.setProperty(
      '--site-header-height',
      `${Math.floor(announcementOffsetHeight)}px`
    );
  }
  updateScrollOffset();
}

// Steuert Scroll-Zustand des Headers, Logo-Wechsel und Start-Header-Animation
export function initHeaderScroll(threshold = 200) {
  const header     = document.querySelector('.site-header');
  const body       = document.body;
  const logoClaim  = document.querySelector('.logo-claim');
  const logoScroll = document.querySelector('.logo-scroll');
  const startHeader = document.querySelector('.start-header');
  const startHeaderInner = startHeader ? startHeader.querySelector('.page-header__inner') : null;

  if (!header) return;

  function setLogoState(scrolled) {
    // erst beide aus
    logoClaim?.classList.remove('show');
    logoScroll?.classList.remove('show');
    // dann genau eines an
    (scrolled ? logoScroll : logoClaim)?.classList.add('show');
  }

  function toggleScrolledClass() {
    const scrolled = window.scrollY > threshold;
    header.classList.toggle('scrolled', scrolled);
    body.classList.toggle('scrolled', scrolled);
    setLogoState(scrolled);

    if (startHeader && startHeaderInner) {
      const headerHeight = header.offsetHeight || 0;
      const stickyHeight = startHeaderInner.offsetHeight || startHeaderInner.getBoundingClientRect().height || 0;
      const threshold = headerHeight + stickyHeight;
      const startRect = startHeader.getBoundingClientRect();
      const parentPassed = startRect.bottom <= threshold;
      body.classList.toggle('start-header-out', parentPassed);
      header.classList.toggle('start-header-out', parentPassed);
    } else {
      body.classList.remove('start-header-out');
      header.classList.remove('start-header-out');
    }
  }

  const debouncedResize = debounce(toggleScrolledClass, 150);

  // Hintergrundvideos mit Energiespar-Fallback automatisch abspielen
  const attemptBackgroundVideoPlayback = (videoEl) => {
    if (!videoEl) return;
    if (!videoEl.paused && videoEl.readyState >= 2) return;

    const startPlayback = () => {
      if (!videoEl.paused) return;
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {});
      }
    };

    if (videoEl.readyState >= 2) {
      startPlayback();
    }
  };

  const prepareBackgroundVideo = (videoEl) => {
    if (!videoEl || videoEl.dataset.bgVideoPrepared === '1') return;

    videoEl.setAttribute('playsinline', '');
    videoEl.setAttribute('muted', '');
    videoEl.setAttribute('autoplay', '');
    videoEl.muted = true;
    videoEl.autoplay = true;
    videoEl.loop = true;

    const onReady = () => attemptBackgroundVideoPlayback(videoEl);
    videoEl.addEventListener('loadeddata', onReady);
    videoEl.addEventListener('canplay', onReady);

    videoEl.dataset.bgVideoPrepared = '1';
  };

  // Startet die Wiedergabe aller registrierten Hintergrundvideos
  const playAllBackgroundVideos = () => {
    document.querySelectorAll('video.bg-video').forEach((videoEl) => {
      prepareBackgroundVideo(videoEl);
      attemptBackgroundVideoPlayback(videoEl);
    });
  };

  // Pausiert alle Hintergrundvideos, z.B. bei Tab-Wechsel
  const pauseAllBackgroundVideos = () => {
    document.querySelectorAll('video.bg-video').forEach((videoEl) => {
      if (!videoEl.paused) videoEl.pause();
    });
  };

  // Stellt sicher, dass Hintergrundvideos bei Interaktionen starten
  const registerBackgroundVideoFallbacks = () => {
    if (window.__bgVideoFallbacksAttached) return;

    const triggerPlayback = () => {
      playAllBackgroundVideos();
    };

    const attachInteractionListeners = () => {
      const body = document.body;
      if (!body) return;
      ['click', 'touchstart', 'keydown'].forEach((eventName) => {
        body.addEventListener(eventName, triggerPlayback, { passive: true });
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attachInteractionListeners, { once: true });
    } else {
      attachInteractionListeners();
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        pauseAllBackgroundVideos();
      } else {
        triggerPlayback();
      }
    }, { passive: true });

    window.addEventListener('focus', triggerPlayback, { passive: true });
    window.addEventListener('pagehide', pauseAllBackgroundVideos, { passive: true });

    window.__bgVideoFallbacksAttached = true;
  };

  playAllBackgroundVideos();
  registerBackgroundVideoFallbacks();

  // Initial sofort korrekt setzen (kein Flash)
  toggleScrolledClass();

  // Danach normal auf Scroll reagieren
  window.addEventListener('scroll', toggleScrolledClass, { passive: true });

  // Bei Fenstergrößenänderung neu bewerten
  window.addEventListener('resize', debouncedResize);

  // bfcache/Back-Navigation: Zustand erneut anwenden
  window.addEventListener('pageshow', () => {
    requestAnimationFrame(() => {
      toggleScrolledClass();
      playAllBackgroundVideos();
    });
  });
}

// Setzt die Höhe des Produkt-Fakten-Blocks als CSS-Variable
// Setzt die Höhe des Produkt-Fakten-Blocks als CSS-Variable
export function setProdFactsHeight() {
  const facts = document.querySelector('.product-header__facts');
  if (facts) {
    document.documentElement.style.setProperty(
      '--product-facts-height',
      `${facts.offsetHeight}px`
    );
  }
  updateScrollOffset();
}

// Fügt auf großen Viewports automatisch Trenner zwischen .section-item-Elementen ein
export function initSectionDividers(containerSelector = '.section__wrapper') {
  const container = document.querySelector(containerSelector);
  if (!container) return;

  function renderDividers() {
    container.querySelectorAll('.divider').forEach(el => el.remove());

    if (window.matchMedia('(min-width: 1081px)').matches) {
      const items = Array.from(container.querySelectorAll('.section-item'));
      items.forEach((item, i) => {
        if (i < items.length - 1) {
          const divider = document.createElement('div');
          divider.className = 'divider w-px bg-primary-1';
          container.insertBefore(divider, item.nextSibling);
        }
      });
    }
  }

  return renderDividers;
}

// Sanftes Scrollen zu Anker-Links inkl. Tab-Unterstützung
export function initSmoothAnchorScroll() {
  const links = Array.from(document.querySelectorAll('a[href*="#"]'));
  if (!links.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const parseAnchor = (link) => {
    const href = link.getAttribute('href');
    if (!href || href === '#') return null;

    if (href.startsWith('#')) return href.substring(1);

    try {
      const url = new URL(href, window.location.href);
      const sameOrigin = url.origin === window.location.origin;
      const normalize = (path) => path.replace(/\/+$/, '') || '/';
      const samePath = normalize(url.pathname) === normalize(window.location.pathname);
      const sameSearch = url.search === window.location.search;
      if (!sameOrigin || !samePath || !sameSearch) return null;
      return url.hash ? url.hash.substring(1) : null;
    } catch (err) {
      return null;
    }
  };

  const scrollToTarget = (targetId, updateHistory = true) => {
    if (!targetId) return;
    let target = document.getElementById(targetId) || document.querySelector(`[name="${escapeSelector(targetId)}"]`);
    if (!target) return;

    const resolveTabActivation = (el) => {
      if (!el) return el;

      const activatePanelFromTab = (tabEl) => {
        if (!tabEl || !tabEl.matches('[role="tab"][data-tabs-target]')) return;
        tabEl.click();
      };

      if (el.matches('[role="tab"][data-tabs-target]')) {
        activatePanelFromTab(el);
        return el.closest('.tabs') || el;
      }

      if (el.matches('[role="tabpanel"]')) {
        const tabId = el.getAttribute('aria-labelledby');
        if (tabId) activatePanelFromTab(document.getElementById(tabId));
        return el;
      }

      const parentPanel = el.closest('[role="tabpanel"]');
      if (parentPanel) {
        const tabId = parentPanel.getAttribute('aria-labelledby');
        if (tabId) activatePanelFromTab(document.getElementById(tabId));
        return parentPanel;
      }

      return el;
    };

    target = resolveTabActivation(target);

    const offset = updateScrollOffset();
    const tabListHeight = getTabsListHeight(target);
    const extraGap = window.matchMedia('(min-width: 768px)').matches ? 64 : 32;
    const tabsOffset = tabListHeight + extraGap;
    const top = window.scrollY + target.getBoundingClientRect().top - offset - tabsOffset;

    window.scrollTo({
      top: top < 0 ? 0 : top,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });

    if (updateHistory) {
      const newHash = `#${targetId}`;
      if (window.location.hash !== newHash) {
        history.pushState(null, '', newHash);
      }
    }
  };

  links.forEach((link) => {
    const anchorId = parseAnchor(link);
    if (!anchorId) return;

    link.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      scrollToTarget(anchorId);
    });
  });

  window.addEventListener('hashchange', () => {
    const id = window.location.hash ? window.location.hash.substring(1) : '';
    if (id) scrollToTarget(id, false);
  });

  if (window.location.hash) {
    const id = window.location.hash.substring(1);
    setTimeout(() => scrollToTarget(id, false), 0);
  }
}

// Scroll-to-top Button sichtbar machen, sobald ausreichend gescrollt wurde
export function initScrollToTop() {
  const trigger = document.querySelector('.scroll-to-top');
  if (!trigger) return;

  const toggleVisibility = () => {
    const shouldShow = window.scrollY > window.innerHeight;
    trigger.classList.toggle('scroll-to-top--visible', shouldShow);
  };

  toggleVisibility();
  window.addEventListener('scroll', toggleVisibility, { passive: true });
  window.addEventListener('pageshow', toggleVisibility);
}

// Sticky Buttons am Footer je nach Scrollrichtung ein-/ausblenden
const STICKY_BUTTON_THRESHOLD = 60;
const STICKY_BUTTON_DELAY = 200;

export function initStickyButtons() {
  const buttons = document.querySelector('.site-footer__sticky-buttons');
  if (!buttons) return;

  let lastScrollY = window.scrollY;
  let scheduledJob = null;

  const setState = (visible) => {
    buttons.classList.toggle('site-footer__sticky-buttons--hidden', !visible);
    buttons.classList.toggle('site-footer__sticky-buttons--visible', visible);
  };

  setState(true);
  buttons.dataset.stickyButtonsReady = '1';

  const onScroll = () => {
    const currentY = window.scrollY;
    const scrollingDown = currentY > lastScrollY && currentY > STICKY_BUTTON_THRESHOLD;
    const nextVisible = !scrollingDown;

    if (scheduledJob) clearTimeout(scheduledJob);
    scheduledJob = setTimeout(() => setState(nextVisible), STICKY_BUTTON_DELAY);

    lastScrollY = currentY;
  };

  window.addEventListener('scroll', onScroll, { passive: true });
}
